import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import editIcon from '../../../assets/editIcon.png';
import trashActive   from '../../../assets/tokens/trashActive.png';
import './Roles.css';
import Button from '../../../components/Button/Button';
import Table from '../../../components/Table/Table';
import ModalAlert from '../../../components/ModalAlert/ModalAlert';
import Modal from '../../../components/Modal/Modal';
import Filters from '../../../components/Filters/Filters';
//services
import { role } from '../../../services/role';
import { modulePermissions } from '../../../utils/modulePermissions';
import Tooltip from '../../../components/Tooltip/Tooltip';

const Roles = () => {
  const navigate = useNavigate();
  const { searchTerm } = useSelector((state) => state.search);
  const [isNameFilterOpen, setIsNameFilterOpen] = useState(false);
  const [isNumberFilterOpen, setIsNumberFilterOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rolesData, setRolesData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedName, setSelectedName] = useState('');
  const [selectedNumber, setSelectedNumber] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [columns, setColumns] = useState([]);
  const [columnsShare, setColumnsShare] = useState(0);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);

  useEffect(() => {
    fetchRoles();
    fetchColumns();
  }, []);

  useEffect(() => {
    // Debounce search to avoid too many requests
    const timeoutId = setTimeout(() => {
      fetchRoles(searchTerm, currentPage);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, currentPage, selectedName, selectedNumber]);

  const fetchRoles = async ( query = '', page = 1) => {
    try {
      const params = {
        page: page,
        items_per_page: 9,
        ...(selectedName && { name: selectedName }),
        ...(selectedNumber && { user_quantity: selectedNumber }),
        ...(query && { query })
      };
      console.log('Fetching roles with params:', params);
      const response = await role.getDataRole(params);
      if(response.status === 'success') {
        const rolesCurrent = response.data.data.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setTotalItems(response.data.total);
        setRolesData(rolesCurrent);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  // Table columns configuration
  const fetchColumns = async () => {
    let count = 0;
    const columnsCurrentUser = [
      { header: 'Name of role', field: 'name' },
      { header: 'Role description', field: 'description' },
      { header: 'Number of users', field: 'user_quantity' },
    ];
    if(modulePermissions.hasRolesPermission('edit')) {
      columnsCurrentUser.push({
        header: '',
        render: (row) => (
          <Tooltip text="Edit this role">
            <button className="edit-btn" onClick={() => handleEdit(row)}>
              <img src={editIcon} alt="Edit" />
            </button>
          </Tooltip>
        )
      });
      count++;
    }
    if(modulePermissions.hasRolesPermission('delete')) {
      columnsCurrentUser.push({
        header: '',
        render: (row) => (
          <Tooltip text="Delete this role">
            <button className="edit-btn" onClick={() => handleAlertDelete(row)}>
              <img src={trashActive} alt="Delete" />
            </button>
          </Tooltip>
        )
      });
      count++;
    }
    setColumns(columnsCurrentUser);
    setColumnsShare(count);
  };

  const handleEdit = (row) => {
    navigate(`/dashboard/role/edit-role/${row.id}`);
  };

  const handleAlertDelete = (row) => {
    setSelectedRole(row);
    setIsModalOpen(true);
  };

  // Action buttons configuration
  const handleNewRole = () => {
    navigate('/dashboard/role/new-role');
  };

  const handleDownload = async () => {
    // Handle roles download
    console.log('Downloading roles...');
    try {
      const params = {
        page: 1,
        items_per_page: 10,
        ...(selectedName && { name: selectedName }),
        ...(selectedNumber && { user_quantity: selectedNumber }),
        ...(searchTerm && { query: searchTerm })
      };
      const response = await role.downloadRoles(params);
      if (response.status === 'success') {
        // Descargar el archivo
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'roles.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading roles:', error);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleDelete = async () => {
    console.log('Deleting role:', selectedRole);
    try {
      if(selectedRole.user_quantity > 0) {
        setIsModalDeleteOpen(true);
        return;
      }
      const response = await role.deleteRole(selectedRole.id);
      if (response.status === 'success') {
        fetchRoles();
      }
    } catch (error) {
      console.error('Error deleting role:', error);
    } finally {
      setIsModalOpen(false);
    }
  };

  return (
    <div className="roles-page">
      <div className="roles-container">
        {/* Actions */}
        <div className="actions-bar">
          <div className="content-filter">
            <Filters
              filters={[
                {
                  id: 'name',
                  label: 'Name',
                  type: 'text',
                  placeholder: 'Enter name'
                },
                {
                  id: 'users',
                  label: 'Number of users',
                  type: 'number',
                  placeholder: 'Enter number'
                }
              ]}
              onApplyFilters={(filters) => {
                console.log('Applying filters:', filters);
                setSelectedName(filters.name || '');
                setSelectedNumber(filters.users || '');
              }}
            />
          </div>
          <div className="action-buttons">
            {(isNumberFilterOpen || isNameFilterOpen) && (
              <Button
                onClick={() => fetchRoles()}
                variant="secondary"
                borderRadius={6}
              >
                Filter
              </Button>
            )}
            {modulePermissions.hasRolesPermission('create') && (
              <Button
                onClick={handleNewRole}
                variant="primary"
                borderRadius={6}
              >
                New role
              </Button>
            )}
            {modulePermissions.hasRolesPermission('download') && (
              <Button
                onClick={handleDownload}
                variant="warning"
                borderRadius={6}
              >
                Download
            </Button>
            )}
          </div>
        </div>

        {/* Roles Table */}
        <Table 
          columns={columns}
          data={rolesData}
          columnsShare={columnsShare}
          itemsPerPage={10}
          currentPage={currentPage}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
        />
        <ModalAlert
          isOpen={isModalOpen}
          onClose={handleClose}
          title={{ text: 'Delete', highlight: 'role' }}
          message="Do you want to continue with the action?"
          confirmText="Accept"
          cancelText="Cancel"
          onConfirm={handleDelete}
        />
        <Modal
          isOpen={isModalDeleteOpen}
          onClose={() => setIsModalDeleteOpen(false)}
          title={{ text: 'Delete', highlight: 'role' }}
          message="This role has users assigned to it."
          error={true}
          onConfirm={() => setIsModalDeleteOpen(false)}
        />
      </div>
    </div>
  );
};

export default Roles;
